/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Spin,
  Form,
  Button,
  Typography,
  Input,
} from 'antd';
import Table from 'components/Table';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import {
  ArrowLeftOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { onError } from 'utils/handlers';
import { getTiposDeDescuentos } from 'api/recaudacion/tiposDeDescuento';
import currencyFormat from 'utils/toCurrency';
import { putDescuentos } from 'api/recaudacion';
import { useSelector } from 'react-redux';

const DEV = process.env.NODE_ENV === 'development';

// Funciones auxiliares para manejar diferentes tipos de entidades
const getEntityType = (record) => {
  const {
    cargo, recargo, actualizacion, gasto,
  } = record;
  if (cargo) return { type: 'cargo', entity: cargo };
  if (recargo) return { type: 'recargo', entity: recargo };
  if (actualizacion) return { type: 'actualizacion', entity: actualizacion };
  if (gasto) return { type: 'gasto', entity: gasto };
  return { type: null, entity: null };
};

const getEntityDescription = (record) => {
  const { entity } = getEntityType(record);
  return entity?.descripcion || '';
};

const getEntityTypeLabel = (record) => {
  const { type } = getEntityType(record);
  const typeLabels = {
    cargo: 'Cargo',
    recargo: 'Recargo',
    actualizacion: 'Actualización',
    gasto: 'Gasto',
  };
  return typeLabels[type] || '';
};

const getEntityAmount = (record) => {
  const { entity } = getEntityType(record);
  return entity?.importe_total ? currencyFormat(entity.importe_total) : '';
};

const ModalAutorizarDescuentosEspeciales = ({
  visible,
  setVisible,
  callback,
  descuentosPorAutorizar,
  esEspecial,
}) => {
  const user = useSelector(({ auth }) => auth.user);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [tiposDeDescuentos, setTiposDeDescuentos] = useState([]);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (visible) {
      const findTipo = (tipo_de_descuento) => tiposDeDescuentos
        .find((e) => e.id === tipo_de_descuento);

      const hasPermission = (tipo_de_descuento) => {
        const tipo = findTipo(tipo_de_descuento);
        return tipo.puede_autorizar;
      };
      setData(descuentosPorAutorizar.map((e) => ({
        ...e,
        disabled: !hasPermission(e.tipo_de_descuento),
      })));
    }
  }, [visible]);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const _tiposDeDescuentos = await getTiposDeDescuentos({ es_descuento_especial: esEspecial });
      if (mounted) {
        setTiposDeDescuentos(_tiposDeDescuentos);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setShowForm(false);
    setSelectedRowKeys([]);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({ disabled: record.disabled }),
  };

  const columns = [
    DEV && {
      titleText: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (val) => val,
    },
    {
      titleText: 'Tipo de Descuento',
      dataIndex: 'tipo_de_descuento',
      key: 'tipo_de_descuento',
      width: 500,
      render: (val) => tiposDeDescuentos.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: '',
      key: '',
      width: 450,
      render: (val, record) => getEntityDescription(record),
    },
    {
      titleText: 'Aplicado sobre',
      dataIndex: '',
      key: '',
      width: 200,
      render: (val, record) => getEntityTypeLabel(record),
    },
    {
      titleText: 'Importe al que se aplica el descuento',
      dataIndex: 'aplicado_a',
      key: 'aplicado_a',
      width: 300,
      align: 'right',
      render: (val, record) => getEntityAmount(record),
    },
    {
      titleText: 'Importe del descuento',
      dataIndex: 'importe_total',
      key: 'importe_total',
      width: 200,
      align: 'right',
      render: (val) => currencyFormat(val || 0),
    },
  ].filter(Boolean);

  const handleOnRowClick = (record) => {
    if (selectedRowKeys.includes(record.id)) {
      const clone = [...selectedRowKeys];
      clone.splice(selectedRowKeys.indexOf(record.id), 1);
      setSelectedRowKeys(clone);
    } else if (!record.disabled) {
      setSelectedRowKeys([...selectedRowKeys, record.id]);
    }
  };

  const onFinish = async (estados_globales) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.estados_globales = estados_globales;
      if (estados_globales === 4) {
        values.usuario_que_autorizo = user.id;
      } else {
        values.estado_del_descuento = 3;
      }
      const promises = data
        .map((e) => putDescuentos({ ...e, cargo: e.cargo.id, ...values }, e.id));
      await Promise.all(promises);
      callback();
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  return (
    <Modal
      visible={visible}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={!loading}
      width={showForm ? 515 : '70%'}
      centered
      title={(
        <FormSubmitCustomControls
          label="Autorizar Descuentos Especiales"
          loading={loading}
          controls={[
            {
              text: 'Regresar',
              icon: <ArrowLeftOutlined />,
              onClick: onCancel,
            },
            {
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              onClick: () => onFinish(5),
              className: 'danger',
              disabled: !selectedRowKeys.length,
            },
            {
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              onClick: () => onFinish(4),
              className: 'success',
              disabled: !selectedRowKeys.length,
            },
          ]}
        />
      )}
    >
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={() => onFinish()}
        >
          <Form.Item label="Motivo de la cancelación" name="motivo_de_cancelacion">
            <Input allowClear />
          </Form.Item>
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
        <Table
          handleOnRowClick={handleOnRowClick}
          cols={columns}
          noControls
          data={data}
          rowSelection={rowSelection}
          hasAllViewPermission
          rowKey="id"
        />
        {data.some((e) => e.disabled) && (
          <Typography.Text type="danger">
            * No es posible autorizar algunos elementos porque no cuenta con los roles necesarios
          </Typography.Text>
        )}
      </Spin>
    </Modal>
  );
};

ModalAutorizarDescuentosEspeciales.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  descuentosPorAutorizar: PropTypes.arrayOf(PropTypes.shape({})),
  esEspecial: PropTypes.bool,
};

ModalAutorizarDescuentosEspeciales.defaultProps = {
  descuentosPorAutorizar: [],
  esEspecial: false,
};

export default ModalAutorizarDescuentosEspeciales;
